import apis from '../../utils/apis';
import { getStationCode } from '../../utils/networkUtil';
import { PLANS, PLAN_STATUS, PLAN_VERSIONS } from '../../utils/constants';
import { buildCreatePlanInput } from './FlowPlannerModel';

/**
 * callAPI never calls reject — on error it invokes the onError callback and
 * the promise hangs forever. This wrapper turns that callback into a reject
 * so callers can use standard try/catch. The Error returned carries the HTTP
 * status code on `err.status` (parsed from the "Result status: NNN" message)
 * so callers can distinguish client errors from transient 5xx.
 */
function withReject(apiCall) {
  return new Promise((resolve, reject) => {
    apiCall((errMsg) => {
      const err = new Error(errMsg);
      const match = /Result status:\s*(\d+)/.exec(errMsg || '');
      if (match) {
        err.status = parseInt(match[1], 10);
      }
      reject(err);
    }).then(resolve, reject);
  });
}

/** Fetches a plan by ID with optional extra query params. */
export function fetchPlan(planId, extraQuery = {}) {
  return withReject((onError) => apis['GET_PLAN']({ query: { planId, ...extraQuery } }, onError));
}

/** Creates a new STATIC_V3 flow plan for the given date with optional overrides. */
export function createFlowPlan(date, overrides, rawInput, dependentPlanId) {
  const input = buildCreatePlanInput(rawInput, overrides);
  return withReject((onError) =>
    apis['CREATE_PLAN'](
      {
        body: {
          nodeId: getStationCode(),
          ofdDate: date,
          planType: PLANS.PRE_SHIFT,
          planVersion: PLAN_VERSIONS.STATIC_V3,
          ...(dependentPlanId ? { dependentPlanId } : {}),
          input
        }
      },
      onError
    )
  );
}

/** Creates a DYNAMIC_V2 optimized plan with the selected preset (preset is optional). */
export function createOptimizedPlan(date, overrides, rawInput, preset, dependentPlanId) {
  const input = buildCreatePlanInput(rawInput, overrides) || {};
  if (preset) {
    input.optimizationParameters = { ...(input.optimizationParameters || {}), preset };
  }
  return withReject((onError) =>
    apis['CREATE_PLAN'](
      {
        body: {
          nodeId: getStationCode(),
          ofdDate: date,
          planType: 'PRE_SHIFT',
          planVersion: PLAN_VERSIONS.DYNAMIC_V2,
          ...(dependentPlanId ? { dependentPlanId } : {}),
          input
        }
      },
      onError
    )
  );
}

/** Approves a plan by ID. */
export function approvePlan(planId) {
  return withReject((onError) =>
    apis['UPDATE_PLAN_STATUS']({ body: { planId, status: PLAN_STATUS.APPROVED } }, onError)
  );
}

/** Rejects a plan by ID. */
export function rejectPlan(planId) {
  return withReject((onError) =>
    apis['UPDATE_PLAN_STATUS']({ body: { planId, status: PLAN_STATUS.REJECTED } }, onError)
  );
}

/**
 * Fetches the Risk Engine evaluation for a plan from SPMS.
 * Backed by the GetRiskEvaluation operation: GET /shift-plans/risk.
 * Response shape mirrors {@link RiskEvaluation} in ./riskEngineModel.
 */
export function getRiskEvaluation(planId) {
  return withReject((onError) =>
    apis['GET_RISK_EVALUATION']({ query: { planId, triggerSource: 'PLAN_GENERATION' } }, onError)
  );
}

/**
 * Adherence (planned vs actual) for a plan on the compressed route, scoped to this
 * station + date + planId. Callers warn when the response carries no
 * timeWindowAdherences — we deliberately do NOT re-query without the planId.
 */
export function fetchAdherence(date, planId) {
  const query = { nodeId: getStationCode(), ofdDate: date, ...(planId ? { planId } : {}) };
  return withReject((onError) => apis['GET_ADHERENCE']({ query }, onError));
}

/**
 * Posts VET/VTO opportunities for an approved V3 plan.
 * Backed by the PostOpportunities operation: POST /shift-plans/plan/opportunities.
 * Server validates: plan is APPROVED + V3 + TPH risk not breached.
 * On failure, rejects with {errorMessage} containing the server error.
 */
export function postOpportunities(planId) {
  return apis['POST_OPPORTUNITIES']({ body: { planId } });
}
